// Wiki-kennis — beheer van de Outline-wiki-index die AI Chat, de Assistant en
// de Offerte-generator als kennisbron gebruiken. Status + sync + zoektest.
// De sync draait server-side als background-task: POST /sync geeft direct een
// run_id terug, daarna pollen we /status tot de run klaar is.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Alert, CircularProgress, Stack, Typography, Paper, TextField,
  Chip, Link, Tooltip,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import CableIcon from '@mui/icons-material/Cable';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { SectionHeader, T } from '../adminSettingsCommon';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

const RUN_STATUS = {
  running: { label: 'Bezig…', color: 'info' },
  completed: { label: 'Geslaagd', color: 'success' },
  partial: { label: 'Deels geslaagd', color: 'warning' },
  failed: { label: 'Mislukt', color: 'error' },
};

function StatBox({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 120, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: T.font, fontSize: '1.3rem', fontWeight: 800, color: T.text }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function WikiKennis() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'test' | 'sync' | 'force'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const [cfgRes, statusRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/wiki/configured`),
        authFetch(`${BACKEND_URL}/Api/wiki/status`),
      ]);
      const cfg = await cfgRes.json().catch(() => ({}));
      const st = await statusRes.json().catch(() => ({}));
      if (cfgRes.ok) setConfig(cfg);
      if (statusRes.ok) setStatus(st);
      if (!cfgRes.ok && !statusRes.ok) {
        setError(cfg.error || st.error || 'Wiki-status ophalen mislukt');
      }
    } catch (e) {
      setError(`Wiki-status ophalen mislukt: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    loadStatus().finally(() => setLoading(false));
  }, [loadStatus]);

  // Zolang er een sync draait: elke 4s de status verversen.
  const runRunning = status?.last_run?.status === 'running';
  useEffect(() => {
    if (!runRunning) return undefined;
    const timer = setInterval(loadStatus, 4000);
    return () => clearInterval(timer);
  }, [runRunning, loadStatus]);

  const testConnection = async () => {
    setBusy('test'); setError(null); setNotice(null); setTestResult(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/wiki/test-connection`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      setTestResult(data);
    } catch (e) {
      setError(`Verbindingstest mislukt: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const startSync = async (force) => {
    setBusy(force ? 'force' : 'sync'); setError(null); setNotice(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/wiki/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 409) {
        setNotice('Er draait al een sync — de status hieronder ververst vanzelf.');
      } else if (!r.ok) {
        throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      } else {
        setNotice(`Sync gestart (run #${data.run_id})${force ? ' — volledige herindexering' : ''}. Voortgang ververst vanzelf.`);
      }
      await loadStatus();
    } catch (e) {
      setError(`Sync starten mislukt: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true); setError(null); setResults(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/wiki/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, limit: 6 }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      setResults(data.results || []);
    } catch (e) {
      setError(`Zoeken mislukt: ${e.message}`);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={22} sx={{ color: T.outlineVariant }} />
      </Box>
    );
  }

  const lastRun = status?.last_run;
  const runMeta = lastRun ? (RUN_STATUS[lastRun.status] || { label: lastRun.status, color: 'default' }) : null;
  const configured = !!(config?.configured ?? status?.configured);

  return (
    <Box>
      <SectionHeader
        title="Wiki-kennis"
        subtitle="De Outline-wiki wordt read-only gesynchroniseerd naar een doorzoekbare index. AI Chat, de Assistant en de Offerte-generator gebruiken die als bron voor werkwijzen, standaarden en learnings."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {!configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          De Outline-API-key is nog niet geconfigureerd op de AI Engine
          (<code>OUTLINE_API_KEY</code>). Zonder key blijft de wiki-kennis uit —
          chat en offertes werken gewoon door.
        </Alert>
      )}

      {/* Status */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <StatBox label="Actieve documenten" value={status?.active_documents} />
        <StatBox label="Inactief" value={status?.inactive_documents} />
        <StatBox label="Fragmenten" value={status?.chunks} />
        <StatBox label="Sync-interval" value={config?.sync_interval_minutes ? `${config.sync_interval_minutes} min` : '—'} />
      </Stack>

      {/* Acties */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined" onClick={testConnection} disabled={!!busy}
          startIcon={busy === 'test' ? <CircularProgress size={16} color="inherit" /> : <CableIcon />}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Test verbinding
        </Button>
        <Button
          variant="contained" onClick={() => startSync(false)} disabled={!!busy || runRunning}
          startIcon={busy === 'sync' ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDim } }}
        >
          Synchroniseer
        </Button>
        <Tooltip title="Herindexeert álle documenten opnieuw (inclusief embeddings) — gebruik dit alleen na wijzigingen in de collectie-allowlist of bij problemen.">
          <span>
            <Button
              variant="outlined" color="warning" onClick={() => startSync(true)} disabled={!!busy || runRunning}
              startIcon={busy === 'force' ? <CircularProgress size={16} color="inherit" /> : <RestartAltIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Volledig herindexeren
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {testResult && (
        (testResult.documents_visible ?? 0) > 0 ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTestResult(null)}>
            Verbinding met Outline OK — de key kan documenten lezen. Het totale
            aantal zie je na een sync in de status hierboven.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTestResult(null)}>
            Verbinding met Outline OK, maar de key ziet géén documenten — check
            de collectie-rechten van de API-key in Outline.
          </Alert>
        )
      )}

      {/* Laatste sync-run */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: T.font }}>
            Laatste sync
          </Typography>
          {runMeta && (
            <Chip
              size="small" label={runMeta.label} color={runMeta.color} variant="outlined"
              icon={lastRun.status === 'running' ? <CircularProgress size={12} /> : undefined}
            />
          )}
        </Box>
        {lastRun ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Gestart {fmtDateTime(lastRun.started_at)}
              {lastRun.completed_at ? ` · klaar ${fmtDateTime(lastRun.completed_at)}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {lastRun.documents_seen ?? 0} gezien · {lastRun.documents_created ?? 0} nieuw ·{' '}
              {lastRun.documents_updated ?? 0} bijgewerkt · {lastRun.documents_unchanged ?? 0} ongewijzigd ·{' '}
              {lastRun.documents_deactivated ?? 0} gedeactiveerd · {lastRun.chunks_written ?? 0} fragmenten
              {lastRun.errors ? ` · ${lastRun.errors} fout(en)` : ''}
            </Typography>
            {lastRun.error_message && (
              <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                {lastRun.error_message}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nog geen sync gedraaid.
          </Typography>
        )}
      </Paper>

      {/* Collecties & rechten — de index volgt de rechten uit Outline */}
      {Array.isArray(status?.collections) && status.collections.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: T.font, mb: 0.25 }}>
            Collecties &amp; rechten
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Rechten komen uit Outline ({status.rights_mode || 'rechten volgen'}). Werkruimte-brede
            collecties zijn voor iedereen; privécollecties alleen voor hun leden, en alleen als
            de ledenlijst leesbaar is.
          </Typography>
          {status.private_skipped_message && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>{status.private_skipped_message}</Alert>
          )}
          <Stack spacing={0.75}>
            {status.collections.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Box
                  component="span"
                  aria-label={c.indexed ? 'geïndexeerd' : 'niet geïndexeerd'}
                  sx={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
                    bgcolor: c.indexed ? 'success.main' : T.outlineVariant,
                  }}
                />
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 200 }}>
                  {c.name || c.id}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                  {c.indexed ? 'Geïndexeerd' : 'Niet geïndexeerd'} · {c.reason}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.documents_indexed ?? 0} / {c.documents_in_outline ?? 0} docs
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Zoektest */}
      <SectionHeader
        title="Zoektest"
        subtitle="Doorzoek de index zoals de AI dat doet (hybride: full-text + semantisch), met jouw leesrechten waar die bekend zijn — anders alleen werkruimte-brede collecties. Handig om te checken of een learning vindbaar is."
      />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small" label="Zoekterm" placeholder="bv. draaidag checklist"
          value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="contained" onClick={runSearch} disabled={searching || query.trim().length < 2}
          startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDim } }}
        >
          Zoek
        </Button>
      </Stack>

      {results && results.length === 0 && (
        <Alert severity="info">Geen relevante fragmenten gevonden voor deze zoekterm.</Alert>
      )}
      {results && results.map((r, i) => (
        <Paper key={`${r.document_id}-${i}`} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: T.font }}>
              {r.url ? (
                <Link href={r.url} target="_blank" rel="noopener noreferrer" underline="hover">
                  {r.title}
                </Link>
              ) : r.title}
            </Typography>
            {r.collection && <Chip size="small" label={r.collection} variant="outlined" />}
            <Typography variant="caption" color="text.secondary">
              score {r.score}
            </Typography>
          </Box>
          {r.heading && r.heading !== r.title && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {r.heading}
            </Typography>
          )}
          <Typography variant="body2" sx={{
            whiteSpace: 'pre-wrap',
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {r.snippet}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}
